import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useCalculos } from "@/hooks/useCalculos";
import { PensaoAlimenticiaResult } from "@/types/calculos";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";

export const PensaoAlimenticia = () => {
  const [salarioLiquido, setSalarioLiquido] = useState("");
  const [percentual, setPercentual] = useState("30");
  const [resultado, setResultado] = useState<PensaoAlimenticiaResult | null>(null);
  
  const { calcularPensaoAlimenticia, loading } = useCalculos();

  useCalculoPrefill("pensao", (v) => {
    setSalarioLiquido(v.salarioLiquido ?? "");
    setPercentual(v.percentual ?? "30");
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!salarioLiquido || !percentual) {
      return;
    }

    try {
      const result = await calcularPensaoAlimenticia({
        salarioLiquido: parseFloat(salarioLiquido),
        percentual: parseFloat(percentual)
      });
      setResultado(result);
      addToHistory({
        type: "pensao",
        label: "Pensão Alimentícia",
        values: { salarioLiquido, percentual },
        summary: `${percentual}% de ${formatCurrency(parseFloat(salarioLiquido))} = ${formatCurrency(result.valorPensao)}`,
      });
    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pensão Alimentícia</CardTitle>
        <p className="text-sm text-muted-foreground">
          Cálculo baseado no percentual do salário líquido
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="salario-liquido">Salário Líquido</Label>
            <Input
              id="salario-liquido"
              type="number"
              placeholder="3000.00"
              value={salarioLiquido}
              onChange={(e) => setSalarioLiquido(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="percentual">Percentual (%)</Label>
            <Input
              id="percentual"
              type="number"
              step="0.1"
              placeholder="30"
              value={percentual}
              onChange={(e) => setPercentual(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Padrão: 30% para 1 filho, 20% para cada filho adicional
            </p>
          </div>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
          disabled={loading}
        >
          {loading ? "Calculando..." : "Calcular Pensão"}
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Valor da Pensão:</h3>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(resultado.valorPensao)}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {percentual}% de {formatCurrency(parseFloat(salarioLiquido))}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};